import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, FileText, User, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const PERIOD_OPTIONS = [
  { value: "last_month", label: "Ultimo mese" },
  { value: "last_quarter", label: "Ultimo trimestre" },
  { value: "last_year", label: "Ultimo anno" },
];

/**
 * StaffEvalReportModal — v109
 * Genera PDF ad-hoc delle schede di valutazione staff (modalità singola o comparativa).
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   initialStaffId?: string  — pre-seleziona 1 dipendente (modalità singola)
 *   defaultHotelId?: string  — filtro hotel pre-impostato (SA)
 */
export default function StaffEvalReportModal({ open, onClose, initialStaffId, defaultHotelId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [mode, setMode] = useState(initialStaffId ? "single" : "single");
  const [period, setPeriod] = useState("last_month");
  const [staffList, setStaffList] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [fHotel, setFHotel] = useState(defaultHotelId || "");
  const [fSearch, setFSearch] = useState("");
  const [selected, setSelected] = useState(initialStaffId ? [initialStaffId] : []);

  // Load staff + hotels on open
  useEffect(() => {
    if (!open) return;
    let aborted = false;
    (async () => {
      setLoading(true);
      try {
        const [sRes, hRes] = await Promise.all([
          api.get("/staff?attivo=true"),
          api.get("/hotels"),
        ]);
        if (aborted) return;
        setStaffList(sRes.data || []);
        setHotels(hRes.data || []);
      } catch (e) {
        if (!aborted) toast.error(formatApiError(e));
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [open]);

  // Reset su chiusura
  useEffect(() => {
    if (!open) {
      setMode(initialStaffId ? "single" : "single");
      setPeriod("last_month");
      setSelected(initialStaffId ? [initialStaffId] : []);
      setFHotel(defaultHotelId || "");
      setFSearch("");
    }
  }, [open, initialStaffId, defaultHotelId]);

  // Quando cambia modalità da multi→single tronca a 1 selezione
  useEffect(() => {
    if (mode === "single" && selected.length > 1) {
      setSelected(selected.slice(0, 1));
    }
  }, [mode, selected]);

  const filteredStaff = useMemo(() => {
    return staffList.filter((s) => {
      if (fHotel && s.hotel_id !== fHotel) return false;
      if (fSearch) {
        const q = fSearch.toLowerCase();
        if (!(`${s.nome} ${s.cognome} ${s.ruolo || ""}`).toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [staffList, fHotel, fSearch]);

  const toggle = (id) => {
    if (mode === "single") {
      setSelected([id]);
      return;
    }
    setSelected((prev) => prev.includes(id)
      ? prev.filter((x) => x !== id)
      : (prev.length >= 20 ? prev : [...prev, id]));
  };

  const canSubmit = (() => {
    if (generating) return false;
    if (mode === "single") return selected.length === 1;
    return selected.length >= 2 && selected.length <= 20;
  })();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setGenerating(true);
    try {
      const params = new URLSearchParams();
      selected.forEach((id) => params.append("employee_ids", id));
      params.append("period", period);
      if (fHotel) params.append("hotel_id", fHotel);
      const res = await api.get(`/staff-evaluations/report-pdf?${params.toString()}`, {
        responseType: "blob",
      });
      // Download blob
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Estrai filename dall'header Content-Disposition se presente
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] : "valutazioni_staff.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report scaricato");
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Errore generazione report");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl" data-testid="staff-eval-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">Genera Report Valutazioni</DialogTitle>
          <DialogDescription>
            Scarica un PDF riepilogativo delle schede di valutazione per uno o più dipendenti.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Step 1 — Modalità */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2 font-medium">1. Modalità</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("single")}
                data-testid="staff-eval-mode-single"
                className={`flex items-start gap-3 rounded-xl border p-3 text-left transition ${mode === "single" ? "border-lavender-500 bg-lavender-50 dark:bg-lavender-950/30" : "border-border hover:border-lavender-300/60"}`}
              >
                <User className={`w-5 h-5 mt-0.5 shrink-0 ${mode === "single" ? "text-lavender-600 dark:text-lavender-300" : "text-muted-foreground"}`} />
                <div>
                  <div className="font-medium text-sm">Scheda singola</div>
                  <div className="text-[11px] text-muted-foreground">Dettaglio storico + trend per 1 dipendente</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setMode("multi")}
                data-testid="staff-eval-mode-multi"
                className={`flex items-start gap-3 rounded-xl border p-3 text-left transition ${mode === "multi" ? "border-lavender-500 bg-lavender-50 dark:bg-lavender-950/30" : "border-border hover:border-lavender-300/60"}`}
              >
                <Users className={`w-5 h-5 mt-0.5 shrink-0 ${mode === "multi" ? "text-lavender-600 dark:text-lavender-300" : "text-muted-foreground"}`} />
                <div>
                  <div className="font-medium text-sm">Report comparativo</div>
                  <div className="text-[11px] text-muted-foreground">Tabella voti medi di più dipendenti (min 2, max 20)</div>
                </div>
              </button>
            </div>
          </section>

          {/* Step 2 — Dipendenti */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">
                2. {mode === "single" ? "Dipendente" : `Dipendenti · selezionati ${selected.length}/20`}
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2">
              {isAdmin && (
                <select
                  value={fHotel}
                  onChange={(e) => setFHotel(e.target.value)}
                  className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs"
                  data-testid="staff-eval-filter-hotel"
                >
                  <option value="">Tutti gli hotel</option>
                  {hotels.map((h) => <option key={h.id} value={h.id}>{h.nome}</option>)}
                </select>
              )}
              <input
                type="text"
                placeholder="Cerca per nome o cognome…"
                value={fSearch}
                onChange={(e) => setFSearch(e.target.value)}
                className="flex-1 rounded-lg border border-border bg-background/60 px-2.5 py-1.5 text-xs"
                data-testid="staff-eval-search"
              />
            </div>
            <div className="rounded-xl border border-border/60 max-h-[280px] overflow-y-auto divide-y divide-border/40">
              {loading ? (
                <div className="p-6 flex items-center justify-center text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Caricamento…
                </div>
              ) : filteredStaff.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground text-center">Nessun dipendente trovato</div>
              ) : (
                filteredStaff.map((s) => {
                  const checked = selected.includes(s.id);
                  const hotel = hotels.find((h) => h.id === s.hotel_id);
                  const atLimit = mode === "multi" && selected.length >= 20 && !checked;
                  return (
                    <label
                      key={s.id}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-accent/40 transition ${checked ? "bg-lavender-50/60 dark:bg-lavender-950/20" : ""} ${atLimit ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid={`staff-eval-item-${s.id}`}
                    >
                      <input
                        type={mode === "single" ? "radio" : "checkbox"}
                        name="staff-eval-pick"
                        checked={checked}
                        disabled={atLimit}
                        onChange={() => toggle(s.id)}
                        className="shrink-0 accent-lavender-600"
                      />
                      <div className="w-8 h-8 rounded-full bg-lavender-200 dark:bg-lavender-900/40 flex items-center justify-center text-[11px] font-semibold shrink-0">
                        {s.nome?.[0] || ""}{s.cognome?.[0] || ""}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{s.nome} {s.cognome}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {s.ruolo || "—"} · {hotel?.nome || "—"}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </section>

          {/* Step 3 — Periodo */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2 font-medium">3. Periodo</div>
            <div className="flex gap-2 flex-wrap">
              {PERIOD_OPTIONS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPeriod(p.value)}
                  data-testid={`staff-eval-period-${p.value.replace("last_", "")}`}
                  className={`px-3.5 py-1.5 rounded-full border text-xs font-medium transition ${period === p.value ? "border-lavender-500 bg-lavender-500 text-white" : "border-border hover:border-lavender-400 hover:bg-accent/40"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/40">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="staff-eval-cancel">
            <X className="w-4 h-4" /> Annulla
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="staff-eval-submit"
            className="bg-lavender-500 hover:bg-lavender-600 text-white gap-2 disabled:opacity-50"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            {generating ? "Generazione in corso…" : "Genera PDF"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
